import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Confirm } from '../../Commons/Modals/Confirm';
import { ApplyCard, GetCardsState, TriggerCardsOverlay } from '../../../utilities/BL/Cards';
import { SendGoalEvent, SendSubstitutionEvent } from '../../../utilities/BL/Score';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';

const Events = ({
    homePlayers = [],
    awayPlayers = [],
    homeTeamName = 'Local',
    awayTeamName = 'Visitante',
    currentMinute = "00:00",
    onScoreChange
}) => {
    const { showMessage } = useFloatMessage();

    // --- ESTADOS DE CONTROL ---
    const [activeCards, setActiveCards] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortCriteria, setSortCriteria] = useState('number'); // 'number' | 'name' | 'lastname' | 'position'
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'
    
    // --- ESTADOS DE DESHACER (PÁNICO) ---
    const [actionHistory, setActionHistory] = useState([]);

    // --- ESTADOS DE CAMBIOS (SUSTITUCIONES) ---
    const [isSubMode, setIsSubMode] = useState(false);
    const [subStep, setSubStep] = useState('out'); // 'out' | 'in'
    const [selectedOutPlayer, setSelectedOutPlayer] = useState(null);
    const [selectedOutTeam, setSelectedOutTeam] = useState(null); // 'home' | 'away'
    const [tempSubQueue, setTempSubQueue] = useState([]); // [{ outPlayer, inPlayer, team: 'home'|'away' }]
    const [isSequentialChangeRunning, setIsSequentialChangeRunning] = useState(false);
    const [subbedPlayerIds, setSubbedPlayerIds] = useState(new Set()); // Jugadores que salieron (banca)

    // --- ESTADOS DEL MODAL DE CONFIRMACIÓN ---
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    // --- REFERENCIA DEL BUCLE DE CAMBIOS SECUENCIALES ---
    const sequenceTimeoutRef = useRef(null);
    const isSequenceCancelled = useRef(false);

    // -------------------------------------------------------------------------
    // Carga inicial del estado de tarjetas desde base de datos
    // -------------------------------------------------------------------------
    useEffect(() => {
        const loadCardsState = async () => {
            const res = await GetCardsState();
            if (res.success && res.data) {
                setActiveCards(res.data);
            }
        };
        loadCardsState();
    }, []);

    // -------------------------------------------------------------------------
    // LÓGICA DE CONFIRMACIÓN (Usando Confirm.js)
    // -------------------------------------------------------------------------
    const requestConfirm = (message, actionFn) => {
        setConfirmModalData({
            message,
            action: async () => {
                try {
                    await actionFn();
                    return { success: true };
                } catch (err) {
                    return { success: false, message: err.message || "Ocurrió un error inesperado." };
                }
            }
        });
        setShowConfirmModal(true);
    };

    // -------------------------------------------------------------------------
    // BUSCADOR Y ORDENACIÓN
    // -------------------------------------------------------------------------
    const sortPlayersList = (list) => {
        return [...list].sort((a, b) => {
            let valA, valB;
            if (sortCriteria === 'number') {
                valA = a.dorsal || 0;
                valB = b.dorsal || 0;
            } else if (sortCriteria === 'name') {
                valA = (a.name || "").split(' ')[0].toLowerCase();
                valB = (b.name || "").split(' ')[0].toLowerCase();
            } else if (sortCriteria === 'lastname') {
                const splitA = (a.name || "").split(' ');
                const splitB = (b.name || "").split(' ');
                valA = (splitA.slice(1).join(' ') || splitA[0]).toLowerCase();
                valB = (splitB.slice(1).join(' ') || splitB[0]).toLowerCase();
            } else if (sortCriteria === 'position') {
                valA = (a.position || "").toLowerCase();
                valB = (b.position || "").toLowerCase();
            }

            if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
            if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const getFilteredPlayers = (players) => {
        let list = [...players];
        if (searchQuery) {
            const query = searchQuery.toLowerCase().trim();
            list = list.filter(p => 
                p.name.toLowerCase().includes(query) || 
                (p.dorsal && p.dorsal.toString().includes(query)) ||
                (p.position && p.position.toLowerCase().includes(query))
            );
        }
        return sortPlayersList(list);
    };

    const filteredHomePlayers = useMemo(() => getFilteredPlayers(homePlayers), [homePlayers, searchQuery, sortCriteria, sortDirection]);
    const filteredAwayPlayers = useMemo(() => getFilteredPlayers(awayPlayers), [awayPlayers, searchQuery, sortCriteria, sortDirection]);

    // -------------------------------------------------------------------------
    // EVENTO: GOL (⚽)
    // -------------------------------------------------------------------------
    const handleGoal = (player, teamType) => {
        requestConfirm(`¿Confirmar GOL de ${player.name} (#${player.dorsal})?`, async () => {
            // Mandamos gol al overlay en la franja con el nombre, dorsal y minuto
            const goalInfo = `#${player.dorsal} ${player.name} (${currentMinute})`;
            await SendGoalEvent(goalInfo);
            
            // Sumamos en marcador principal
            const targetTeam = teamType === 'Local' ? 'home' : 'away';
            onScoreChange(targetTeam, 1);

            // Guardamos en el historial para deshacer
            setActionHistory(prev => [
                ...prev, 
                { type: 'goal', player, team: targetTeam, timestamp: Date.now() }
            ]);

            showMessage("¡Gol registrado!", MESSAGE_TYPE.SUCCESS);
        });
    };

    // -------------------------------------------------------------------------
    // EVENTO: TARJETAS (🟨 / 🟥)
    // -------------------------------------------------------------------------
    const handleCard = (player, teamType, cardColor) => {
        const existingCard = activeCards.find(c => c.playerId === player.id);
        const currentYellow = existingCard?.yellowCardsCount || 0;
        
        let newYellow = currentYellow;
        let newRed = existingCard?.isRedCarded || false;
        let modalText = "";

        if (cardColor === 'yellow') {
            if (currentYellow === 0) {
                newYellow = 1;
                modalText = `¿Aplicar TARJETA AMARILLA a ${player.name} (#${player.dorsal})?`;
            } else if (currentYellow === 1) {
                // Doble Amarilla = Expulsión automática (Roja)
                newYellow = 2;
                newRed = true;
                modalText = `¿Aplicar SEGUNDA AMARILLA a ${player.name} (#${player.dorsal})? Se registrará como Expulsión por Doble Amarilla.`;
            } else {
                showMessage("Este jugador ya ha sido expulsado.", MESSAGE_TYPE.WARNING);
                return;
            }
        } else if (cardColor === 'red') {
            if (newRed) {
                showMessage("Este jugador ya tiene una tarjeta roja.", MESSAGE_TYPE.WARNING);
                return;
            }
            newRed = true;
            modalText = `¿Aplicar TARJETA ROJA DIRECTA a ${player.name} (#${player.dorsal})?`;
        }

        requestConfirm(modalText, async () => {
            const payload = {
                playerId: player.id,
                teamType: teamType === 'Local' ? 'Local' : 'Away',
                yellowCardsCount: newYellow,
                isRedCarded: newRed,
                matchMinute: currentMinute || "0'",
                infractionTitle: newRed && newYellow === 2 ? "Doble Tarjeta Amarilla" : (newRed ? "Tarjeta Roja Directa" : "Tarjeta Amarilla")
            };

            const res = await ApplyCard(payload);
            if (res.success) {
                // Sincronizamos la lista local
                const resState = await GetCardsState();
                if (resState.success) setActiveCards(resState.data);

                // Guardamos en el historial para deshacer
                setActionHistory(prev => [
                    ...prev,
                    { 
                        type: 'card', 
                        player, 
                        team: teamType, 
                        previousState: existingCard ? { ...existingCard } : null,
                        timestamp: Date.now()
                    }
                ]);

                showMessage(payload.infractionTitle + " aplicada correctamente.", MESSAGE_TYPE.SUCCESS);
            } else {
                showMessage("Error al aplicar la tarjeta.", MESSAGE_TYPE.ERROR);
            }
        });
    };

    // -------------------------------------------------------------------------
    // EVENTO: REMOVER TARJETAS (🧹)
    // -------------------------------------------------------------------------
    const handleRemoveCard = (player, teamType) => {
        requestConfirm(`¿Deseas remover las tarjetas aplicadas a ${player.name} (#${player.dorsal})?`, async () => {
            const payload = {
                playerId: player.id,
                teamType: teamType === 'Local' ? 'Local' : 'Away',
                yellowCardsCount: 0,
                isRedCarded: false,
                matchMinute: "0'",
                infractionTitle: "Remover Sanciones"
            };

            const res = await ApplyCard(payload);
            if (res.success) {
                const resState = await GetCardsState();
                if (resState.success) setActiveCards(resState.data);
                showMessage("Sanciones limpiadas.", MESSAGE_TYPE.SUCCESS);
            } else {
                showMessage("Error al remover la sanción.", MESSAGE_TYPE.ERROR);
            }
        });
    };

    // -------------------------------------------------------------------------
    // BOTÓN DE PÁNICO (DESHACER ÚLTIMO EVENTO)
    // -------------------------------------------------------------------------
    const handleUndoLastAction = () => {
        if (actionHistory.length === 0) {
            showMessage("No hay eventos recientes para deshacer.", MESSAGE_TYPE.WARNING);
            return;
        }

        const lastAction = actionHistory[actionHistory.length - 1];
        
        requestConfirm(`¿Deshacer la última acción de ${lastAction.type === 'goal' ? 'GOL' : 'TARJETA'} para ${lastAction.player.name}?`, async () => {
            if (lastAction.type === 'goal') {
                // Restamos gol del marcador
                onScoreChange(lastAction.team, -1);
                
                // Quitamos el evento del stack
                setActionHistory(prev => prev.slice(0, -1));
                showMessage("Gol revertido.", MESSAGE_TYPE.SUCCESS);
            } else if (lastAction.type === 'card') {
                // Restauramos el estado disciplinario previo
                let payload;
                if (lastAction.previousState) {
                    payload = {
                        playerId: lastAction.player.id,
                        teamType: lastAction.team === 'Local' ? 'Local' : 'Away',
                        yellowCardsCount: lastAction.previousState.yellowCardsCount,
                        isRedCarded: lastAction.previousState.isRedCarded,
                        matchMinute: lastAction.previousState.matchMinute,
                        infractionTitle: "DESHACER"
                    };
                } else {
                    // Si no había tarjeta previa, mandamos limpiar
                    payload = {
                        playerId: lastAction.player.id,
                        teamType: lastAction.team === 'Local' ? 'Local' : 'Away',
                        yellowCardsCount: 0,
                        isRedCarded: false,
                        matchMinute: "0'",
                        infractionTitle: "DESHACER"
                    };
                }

                const res = await ApplyCard(payload);
                if (res.success) {
                    const resState = await GetCardsState();
                    if (resState.success) setActiveCards(resState.data);
                    
                    setActionHistory(prev => prev.slice(0, -1));
                    showMessage("Tarjeta revertida.", MESSAGE_TYPE.SUCCESS);
                } else {
                    showMessage("Error al revertir la tarjeta.", MESSAGE_TYPE.ERROR);
                }
            }
        });
    };

    // -------------------------------------------------------------------------
    // SUSTITUCIONES: LÓGICA DE COLA INTERACTIVA
    // -------------------------------------------------------------------------
    const handlePlayerClickInSubMode = (player, teamType) => {
        if (subStep === 'out') {
            // Verificar si el jugador ya está en la banca
            if (subbedPlayerIds.has(player.id)) {
                showMessage("Este jugador ya se encuentra en la banca.", MESSAGE_TYPE.WARNING);
                return;
            }
            // Verificar si el jugador ya está en la cola actual
            if (tempSubQueue.some(item => item.outPlayer.id === player.id)) {
                showMessage("Este jugador ya está seleccionado para salir.", MESSAGE_TYPE.WARNING);
                return;
            }

            setSelectedOutPlayer(player);
            setSelectedOutTeam(teamType);
            setSubStep('in');
        } else if (subStep === 'in') {
            // Debe pertenecer al mismo equipo
            if (teamType !== selectedOutTeam) {
                showMessage("El jugador que entra debe pertenecer al mismo equipo.", MESSAGE_TYPE.WARNING);
                return;
            }
            // El que entra no puede ser el mismo que sale
            if (player.id === selectedOutPlayer.id) {
                showMessage("Un jugador no puede sustituirse a sí mismo.", MESSAGE_TYPE.WARNING);
                return;
            }
            // El suplente no puede estar ya programado para entrar
            if (tempSubQueue.some(item => item.inPlayer.id === player.id)) {
                showMessage("Este suplente ya está programado para ingresar.", MESSAGE_TYPE.WARNING);
                return;
            }

            // Agregamos el par a la cola
            setTempSubQueue(prev => [
                ...prev,
                { 
                    outPlayer: selectedOutPlayer, 
                    inPlayer: player, 
                    team: teamType,
                    pairNumber: prev.length + 1
                }
            ]);

            // Reseteamos paso
            setSelectedOutPlayer(null);
            setSelectedOutTeam(null);
            setSubStep('out');
            showMessage("Par de cambios agregado al resumen.", MESSAGE_TYPE.SUCCESS);
        }
    };

    const handleRemovePairFromQueue = (index) => {
        setTempSubQueue(prev => {
            const list = prev.filter((_, i) => i !== index);
            // Re-indexar números de par
            return list.map((item, idx) => ({ ...item, pairNumber: idx + 1 }));
        });
    };

    const handleCancelSubMode = () => {
        setIsSubMode(false);
        setTempSubQueue([]);
        setSelectedOutPlayer(null);
        setSelectedOutTeam(null);
        setSubStep('out');
    };

    // -------------------------------------------------------------------------
    // BUCLE SECUENCIAL DE MOSTRAR CAMBIOS EN OBS
    // -------------------------------------------------------------------------
    const runSubstitutionsSequence = () => {
        if (tempSubQueue.length === 0) {
            showMessage("Agrega al menos una sustitución primero.", MESSAGE_TYPE.WARNING);
            return;
        }

        requestConfirm("¿Deseas lanzar la secuencia de cambios en vivo a la transmisión?", async () => {
            setIsSubMode(false);
            setIsSequentialChangeRunning(true);
            isSequenceCancelled.current = false;
            
            const queue = [...tempSubQueue];
            setTempSubQueue([]); // Limpiamos la interfaz de cola

            showMessage("Secuencia de cambios iniciada en el Overlay.", MESSAGE_TYPE.SUCCESS);

            const displayPair = async (index) => {
                if (isSequenceCancelled.current || index >= queue.length) {
                    setIsSequentialChangeRunning(false);
                    return;
                }

                const { outPlayer, inPlayer } = queue[index];

                // 1. Mandamos el WebSocket para esta pareja.
                // Usamos una duración de 6 segundos en pantalla, y esperamos 9 segundos totales en el timer.
                // Esto garantiza 6s mostrándose, que se oculte y que queden 3s de pantalla limpia antes del siguiente.
                await SendSubstitutionEvent(
                    outPlayer.dorsal.toString(),
                    outPlayer.name,
                    inPlayer.dorsal.toString(),
                    inPlayer.name,
                    6 // durationSeconds = 6s
                );

                // 2. Marcamos físicamente a los jugadores en banca
                setSubbedPlayerIds(prev => {
                    const next = new Set(prev);
                    next.add(outPlayer.id);
                    next.delete(inPlayer.id); // Si el suplente estaba en banca, ahora está en cancha
                    return next;
                });

                // 3. Esperamos los 9 segundos totales del ciclo
                sequenceTimeoutRef.current = setTimeout(() => {
                    displayPair(index + 1);
                }, 9000);
            };

            displayPair(0);
        });
    };

    // Detener de golpe la transmisión secuencial de cambios
    const handleStopSubstitutions = () => {
        requestConfirm("¿Detener la secuencia de cambios y limpiar el overlay en OBS?", async () => {
            isSequenceCancelled.current = true;
            if (sequenceTimeoutRef.current) {
                clearTimeout(sequenceTimeoutRef.current);
            }
            
            // Enviamos el cierre directo del overlay
            await TriggerCardsOverlay('close');

            setIsSequentialChangeRunning(false);
            showMessage("Secuencia de cambios cancelada en OBS.", MESSAGE_TYPE.WARNING);
        });
    };

    // -------------------------------------------------------------------------
    // COMANDOS DE OBS OVERLAYS DIRECTOS (OCULTAR TARJETA)
    // -------------------------------------------------------------------------
    const handleHideOverlayCard = () => {
        requestConfirm("¿Ocultar overlay de tarjeta en OBS?", async () => {
            // Mandamos cerrar las tarjetas activas en OBS usando el canal directo de trigger
            const res = await TriggerCardsOverlay('close');
            if (res.success) {
                showMessage("Overlay de tarjeta ocultado.", MESSAGE_TYPE.SUCCESS);
            } else {
                showMessage("Error al enviar señal al overlay.", MESSAGE_TYPE.ERROR);
            }
        });
    };

    // -------------------------------------------------------------------------
    // RENDERS AUXILIARES
    // -------------------------------------------------------------------------
    const renderPlayerCard = (player, teamType) => {
        const existingCard = activeCards.find(c => c.playerId === player.id);
        const isYellow = existingCard?.yellowCardsCount === 1;
        const isDoubleYellow = existingCard?.yellowCardsCount === 2;
        const isRed = existingCard?.isRedCarded && !isDoubleYellow;
        
        const isOutInQueue = tempSubQueue.some(item => item.outPlayer.id === player.id);
        const isInInQueue = tempSubQueue.some(item => item.inPlayer.id === player.id);
        const queueIndex = tempSubQueue.findIndex(item => item.outPlayer.id === player.id || item.inPlayer.id === player.id);
        
        const isSubbedOut = subbedPlayerIds.has(player.id);

        let cardClass = "col-6 col-sm-4 col-md-3 col-lg-2 px-1 mb-2";
        let innerStyle = {
            border: '2px solid #dee2e6',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.2s ease',
            opacity: isSubbedOut ? 0.5 : 1
        };

        if (isSubMode) {
            if (isOutInQueue) {
                innerStyle.border = '2px solid #ef4444';
                innerStyle.backgroundColor = '#fee2e2';
            } else if (isInInQueue) {
                innerStyle.border = '2px solid #22c55e';
                innerStyle.backgroundColor = '#dcfce7';
            } else if (selectedOutPlayer?.id === player.id) {
                innerStyle.border = '2px dashed #ef4444';
                innerStyle.backgroundColor = '#ffe4e6';
            }
        } else {
            if (isDoubleYellow || isRed) {
                innerStyle.border = '2px solid #ef4444';
                innerStyle.backgroundColor = '#fef2f2';
            } else if (isYellow) {
                innerStyle.border = '2px solid #facc15';
                innerStyle.backgroundColor = '#fefce8';
            }
        }

        const photoUrl = player.photoUrl ? `/img/games/cards/players/${player.photoUrl}` : null;

        return (
            <div key={player.id} className={cardClass}>
                <div style={innerStyle} className="shadow-sm">
                    {/* Botones / Indicadores superiores (Diseño idéntico a Mockup) */}
                    <div className="d-flex justify-content-between align-items-center p-2 bg-light border-bottom" style={{ height: '38px' }}>
                        {isSubMode ? (
                            // Modo cambios: botones para seleccionar
                            <div className="w-100 text-center">
                                {isOutInQueue && <span className="badge bg-danger">Sale ⬇ (Par {queueIndex + 1})</span>}
                                {isInInQueue && <span className="badge bg-success">Entra ⬆ (Par {queueIndex + 1})</span>}
                                {!isOutInQueue && !isInInQueue && (
                                    <button 
                                        type="button" 
                                        className={`btn btn-xs py-0 px-2 fw-bold text-uppercase ${subStep === 'out' ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                        style={{ fontSize: '0.65rem' }}
                                        onClick={() => handlePlayerClickInSubMode(player, teamType)}
                                    >
                                        {selectedOutPlayer?.id === player.id ? 'Cancelar' : (subStep === 'out' ? 'Sale ⬇' : 'Entra ⬆')}
                                    </button>
                                )}
                            </div>
                        ) : (
                            // Modo estándar: #Dorsal a la izquierda y Botones en bloque a la derecha
                            <>
                                <div className="fw-bold text-dark font-monospace" style={{ fontSize: '1.05rem', lineHeight: 1 }}>
                                    #{player.dorsal}
                                </div>
                                <div className="d-flex gap-1 align-items-center">
                                    {/* Botón Gol G */}
                                    <button 
                                        type="button"
                                        className="btn btn-xs p-0 text-white d-flex align-items-center justify-content-center border-0"
                                        style={{ width: '22px', height: '22px', backgroundColor: '#22c55e', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '800' }}
                                        title="Gol" 
                                        onClick={() => handleGoal(player, teamType)}
                                    >
                                        G
                                    </button>
                                    
                                    {/* Botón Tarjeta Amarilla */}
                                    <button 
                                        type="button"
                                        className="btn btn-xs p-0 d-flex align-items-center justify-content-center"
                                        style={{ 
                                            width: '22px', 
                                            height: '22px', 
                                            backgroundColor: isYellow ? '#facc15' : 'transparent', 
                                            border: '2px solid #facc15',
                                            borderRadius: '4px',
                                            color: isYellow ? '#000' : '#facc15',
                                            fontSize: '0.7rem',
                                            fontWeight: '800'
                                        }}
                                        title="Tarjeta Amarilla" 
                                        onClick={() => handleCard(player, teamType, 'yellow')}
                                    >
                                        {isYellow ? '1' : isDoubleYellow ? '2' : ''}
                                    </button>

                                    {/* Botón Tarjeta Roja */}
                                    <button 
                                        type="button"
                                        className="btn btn-xs p-0 d-flex align-items-center justify-content-center"
                                        style={{ 
                                            width: '22px', 
                                            height: '22px', 
                                            backgroundColor: (isRed || isDoubleYellow) ? '#ef4444' : 'transparent', 
                                            border: '2px solid #ef4444',
                                            borderRadius: '4px',
                                            color: (isRed || isDoubleYellow) ? '#fff' : '#ef4444',
                                            fontSize: '0.7rem',
                                            fontWeight: '800'
                                        }}
                                        title="Tarjeta Roja" 
                                        onClick={() => handleCard(player, teamType, 'red')}
                                    >
                                        {(isRed || isDoubleYellow) ? 'R' : ''}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Cuerpo de la carta */}
                    <div className="text-center py-2 px-1 position-relative">
                        {/* Indicadores visuales de tarjetas activas para limpiar */}
                        {!isSubMode && (isYellow || isDoubleYellow || isRed) && (
                            <div className="position-absolute d-flex gap-1" style={{ top: '4px', right: '6px', zIndex: 5 }}>
                                <button 
                                    className="btn p-0 border-0 bg-transparent text-secondary hover-text-danger" 
                                    title="Quitar tarjetas"
                                    onClick={() => handleRemoveCard(player, teamType)}
                                    style={{ fontSize: '0.75rem', lineHeight: 1 }}
                                >
                                    <i className="bi bi-trash-fill text-muted"></i>
                                </button>
                            </div>
                        )}

                        {/* Foto / Silueta */}
                        <div 
                            style={{ 
                                width: '56px', 
                                height: '56px', 
                                margin: '8px auto', 
                                borderRadius: '50%', 
                                backgroundColor: '#f1f5f9',
                                backgroundImage: photoUrl ? `url(${photoUrl})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6'
                            }}
                        >
                            {!photoUrl && <i className="bi bi-person-fill text-secondary" style={{ fontSize: '1.8rem' }}></i>}
                        </div>
                    </div>

                    {/* Bloque de Nombre y Posición en Negro (Estilo Premium Mockup) */}
                    <div className="bg-dark text-white text-center py-2" style={{ borderBottomLeftRadius: '14px', borderBottomRightRadius: '14px', minHeight: '52px' }}>
                        <div className="text-truncate px-1 fw-bold" style={{ fontSize: '0.8rem' }} title={player.name}>
                            {player.name}
                        </div>
                        <div className="small opacity-75" style={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                            {player.position ? player.position : 'Jugador'}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // -------------------------------------------------------------------------
    // RENDER PRINCIPAL
    // -------------------------------------------------------------------------
    return (
        <div className="card shadow-sm border-0 rounded-4 mt-4" style={{ backgroundColor: '#fafafa' }}>
            <div className="card-header bg-dark text-white fw-bold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-people-fill text-info"></i> Matriz de Jugadores y Eventos en Vivo</span>
                <div className="d-flex gap-2">
                    <button 
                        type="button" 
                        className="btn btn-outline-warning btn-xs fw-bold text-dark bg-warning border-0"
                        onClick={handleUndoLastAction}
                        disabled={actionHistory.length === 0}
                        style={{ fontSize: '0.75rem' }}
                    >
                        <i className="bi bi-arrow-counterclockwise"></i> Deshacer Último Evento
                    </button>
                    {isSequentialChangeRunning && (
                        <button 
                            type="button" 
                            className="btn btn-danger btn-xs fw-bold"
                            onClick={handleStopSubstitutions}
                            style={{ fontSize: '0.75rem' }}
                        >
                            <i className="bi bi-stop-circle-fill"></i> Detener cambios
                        </button>
                    )}
                </div>
            </div>

            <div className="card-body">
                {/* --- SECCIÓN SUPERIOR DE FILTROS --- */}
                <div className="row g-3 align-items-center mb-4 bg-white p-3 rounded-3 shadow-sm border border-light mx-0">
                    {/* Búsqueda */}
                    <div className="col-md-4">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-light border-end-0"><i className="bi bi-search"></i></span>
                            <input 
                                type="text" 
                                className="form-control border-start-0" 
                                placeholder="Buscar por dorsal, nombre o posición..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Criterio Ordenar */}
                    <div className="col-md-5 d-flex align-items-center gap-2">
                        <span className="small text-muted fw-bold text-nowrap">Ordenar por:</span>
                        <div className="btn-group btn-group-sm flex-fill" role="group">
                            {['number', 'name', 'lastname', 'position'].map(crit => (
                                <button 
                                    key={crit}
                                    type="button" 
                                    className={`btn ${sortCriteria === crit ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setSortCriteria(crit)}
                                    style={{ fontSize: '0.7rem' }}
                                >
                                    {crit === 'number' ? 'Número' : crit === 'name' ? 'Nombre' : crit === 'lastname' ? 'Apellido' : 'Posición'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Dirección Ordenar */}
                    <div className="col-md-3 d-flex gap-2">
                        <button 
                            type="button" 
                            className={`btn btn-sm w-50 fw-bold ${sortDirection === 'asc' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => setSortDirection('asc')}
                            style={{ fontSize: '0.7rem' }}
                        >
                            Ascendente
                        </button>
                        <button 
                            type="button" 
                            className={`btn btn-sm w-50 fw-bold ${sortDirection === 'desc' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => setSortDirection('desc')}
                            style={{ fontSize: '0.7rem' }}
                        >
                            Descendente
                        </button>
                    </div>
                </div>

                {/* --- SECCIÓN ACCIONES GLOBALES DE EVENTOS --- */}
                <div className="d-flex gap-2 mb-4 justify-content-center flex-wrap">
                    {isSubMode ? (
                        <>
                            <button className="btn btn-success fw-bold px-4" onClick={runSubstitutionsSequence} disabled={tempSubQueue.length === 0}>
                                <i className="bi bi-check-circle-fill"></i> Listo, mostrar cambios
                            </button>
                            <button className="btn btn-outline-danger fw-bold" onClick={handleCancelSubMode}>
                                <i className="bi bi-x-circle"></i> Cancelar cambios
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="btn btn-outline-primary fw-bold" onClick={() => setIsSubMode(true)}>
                                <i className="bi bi-arrow-left-right"></i> Cambios
                            </button>
                            <button className="btn btn-outline-dark fw-bold" onClick={handleHideOverlayCard}>
                                <i className="bi bi-eye-slash-fill"></i> Ocultar tarjeta
                            </button>
                        </>
                    )}
                </div>

                {/* --- RESUMEN DE CAMBIOS --- */}
                {isSubMode && (
                    <div className="bg-light p-3 rounded-3 border mb-4 text-center">
                        <h6 className="fw-bold mb-2 text-secondary text-uppercase" style={{ fontSize: '0.75rem' }}>Resumen de cambios:</h6>
                        
                        {tempSubQueue.length === 0 ? (
                            <div className="text-muted small">No hay cambios programados aún.</div>
                        ) : (
                            <div className="d-flex flex-wrap gap-2 justify-content-center mb-2">
                                {tempSubQueue.map((item, idx) => (
                                    <div key={idx} className="badge bg-white border text-dark p-2 d-flex align-items-center gap-1 shadow-sm rounded-pill">
                                        <span className="text-danger fw-bold">#{item.outPlayer.dorsal} {item.outPlayer.name.split(' ')[0]} ⬇</span>
                                        <span className="text-muted">|</span>
                                        <span className="text-success fw-bold">#{item.inPlayer.dorsal} {item.inPlayer.name.split(' ')[0]} ⬆</span>
                                        <button 
                                            type="button" 
                                            className="btn p-0 ms-1 border-0 bg-transparent"
                                            onClick={() => handleRemovePairFromQueue(idx)}
                                            style={{ color: '#ef4444', lineHeight: 1 }}
                                        >
                                            <i className="bi bi-x-circle-fill"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="fw-bold text-primary mt-2 small">
                            {selectedOutPlayer ? (
                                <>¿Quién entra por <span className="text-danger">{selectedOutPlayer.name}</span>? Selecciona al jugador suplente de abajo.</>
                            ) : (
                                <>¿Quién sale? Selecciona al jugador titular que abandonará el campo.</>
                            )}
                        </div>
                    </div>
                )}

                {/* --- CUADRÍCULA JUGADORES EQUIPO LOCAL --- */}
                <div className="mb-4">
                    <div className="d-flex align-items-center mb-2 border-bottom pb-1">
                        <h5 className="fw-bold text-primary mb-0"><i className="bi bi-shield-shaded"></i> {homeTeamName}</h5>
                        <span className="badge bg-secondary ms-2">Local</span>
                    </div>
                    {filteredHomePlayers.length === 0 ? (
                        <div className="p-4 text-center text-muted border border-dashed rounded-3">
                            No se encontraron jugadores que coincidan con la búsqueda.
                        </div>
                    ) : (
                        <div className="row g-2 overflow-auto py-1 flex-nowrap" style={{ WebkitOverflowScrolling: 'touch' }}>
                            {filteredHomePlayers.map(p => renderPlayerCard(p, 'Local'))}
                        </div>
                    )}
                </div>

                {/* --- CUADRÍCULA JUGADORES EQUIPO VISITANTE --- */}
                <div>
                    <div className="d-flex align-items-center mb-2 border-bottom pb-1">
                        <h5 className="fw-bold text-danger mb-0"><i className="bi bi-shield-shaded"></i> {awayTeamName}</h5>
                        <span className="badge bg-secondary ms-2">Visita</span>
                    </div>
                    {filteredAwayPlayers.length === 0 ? (
                        <div className="p-4 text-center text-muted border border-dashed rounded-3">
                            No se encontraron jugadores que coincidan con la búsqueda.
                        </div>
                    ) : (
                        <div className="row g-2 overflow-auto py-1 flex-nowrap" style={{ WebkitOverflowScrolling: 'touch' }}>
                            {filteredAwayPlayers.map(p => renderPlayerCard(p, 'Away'))}
                        </div>
                    )}
                </div>
            </div>

            {/* --- MODAL CONFIRMACIÓN --- */}
            {showConfirmModal && confirmModalData && (
                <Confirm
                    message={confirmModalData.message}
                    actionToYesButton={confirmModalData.action}
                    textYesButton="Sí, confirmar"
                    textNoButton="No, cancelar"
                    setShowModal={setShowConfirmModal}
                    setConfirmData={() => {}}
                />
            )}
        </div>
    );
};

export { Events };
